import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { Button, Input, LoginStepContainer, useB3Config } from "../../../../../global-account/react/index.js";
import { debugB3React } from "../../../../../shared/utils/debug.js";
import { useState } from "react";
import { EmailVerificationRequiredError, useBetterAuth } from "../../../hooks/useBetterAuth.js";
import { AuthButton } from "../components/AuthButton.js";
import { PasswordInput } from "../components/PasswordInput.js";
const debug = debugB3React("LoginStepBetterAuth");
const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const SOCIAL_PROVIDERS = [{
  id: "google",
  label: "Google"
}, {
  id: "github",
  label: "GitHub"
}, {
  id: "discord",
  label: "Discord"
}, {
  id: "microsoft",
  label: "Microsoft"
}, {
  id: "slack",
  label: "Slack"
}];
export function LoginStepBetterAuth({
  onSuccess,
  onError,
  verifyEmailRedirectTo
}) {
  const {
    partnerId
  } = useB3Config();
  const {
    signInWithEmail,
    signUpWithEmail,
    signInWithSocial,
    requestPasswordReset
  } = useBetterAuth();
  const [mode, setMode] = useState("sign-in");
  const [showEmailForm, setShowEmailForm] = useState(false);
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [name, setName] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [resetEmailSent, setResetEmailSent] = useState(false);
  const [verificationSent, setVerificationSent] = useState(false);
  const handleEmailSubmit = async () => {
    const normalizedEmail = email.trim().toLowerCase();
    if (!EMAIL_REGEX.test(normalizedEmail)) {
      setError("Please enter a valid email address");
      return;
    }
    if (!password) {
      setError("Please enter a password");
      return;
    }
    if (mode === "sign-up" && !name.trim()) {
      setError("Please enter your name");
      return;
    }
    try {
      setIsLoading(true);
      setError(null);
      if (mode === "sign-up") {
        debug("Signing up", {
          email: normalizedEmail,
          name: name.trim()
        });
        await signUpWithEmail(normalizedEmail, password, name.trim(), verifyEmailRedirectTo);
      } else {
        debug("Signing in", {
          email: normalizedEmail
        });
        await signInWithEmail(normalizedEmail, password);
      }
      debug("Auth successful");
      onSuccess?.();
    } catch (err) {
      if (err instanceof EmailVerificationRequiredError) {
        setVerificationSent(true);
        setError(null);
      } else {
        const message = err instanceof Error ? err.message : "Authentication failed";
        setError(message);
        await onError?.(err);
      }
      debug("Auth failed", err);
    } finally {
      setPassword("");
      setIsLoading(false);
    }
  };
  const handleForgotPassword = async () => {
    const normalizedEmail_0 = email.trim().toLowerCase();
    if (!EMAIL_REGEX.test(normalizedEmail_0)) {
      setError("Please enter a valid email address");
      return;
    }
    try {
      setIsLoading(true);
      setError(null);
      await requestPasswordReset(normalizedEmail_0);
      setResetEmailSent(true);
    } catch (err_0) {
      const message_0 = err_0 instanceof Error ? err_0.message : "Failed to send reset email";
      setError(message_0);
    } finally {
      setIsLoading(false);
    }
  };
  const handleSocialSignIn = async provider => {
    try {
      setIsLoading(true);
      setError(null);
      await signInWithSocial(provider);
    } catch (err_1) {
      const message_1 = err_1 instanceof Error ? err_1.message : `${provider} sign in failed`;
      setError(message_1);
      await onError?.(err_1);
      setIsLoading(false);
    }
  };
  if (verificationSent) {
    return _jsx(LoginStepContainer, {
      partnerId: partnerId,
      children: _jsxs("div", {
        className: "mb-6 w-full space-y-4 px-3 text-center",
        children: [_jsx("div", {
          className: "mx-auto flex h-12 w-12 items-center justify-center rounded-full bg-green-100",
          children: _jsx("svg", {
            className: "h-6 w-6 text-green-600",
            fill: "none",
            viewBox: "0 0 24 24",
            stroke: "currentColor",
            children: _jsx("path", {
              strokeLinecap: "round",
              strokeLinejoin: "round",
              strokeWidth: 2,
              d: "M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z"
            })
          })
        }), _jsx("p", {
          className: "text-sm font-medium text-gray-900 dark:text-gray-100",
          children: "Check your email"
        }), _jsxs("p", {
          className: "text-xs text-gray-500 dark:text-gray-400",
          children: ["We sent a verification link to ", _jsx("span", {
            className: "font-medium text-gray-700 dark:text-gray-200",
            children: email
          }), ". Verify your account, then come back and sign in."]
        }), _jsx("button", {
          onClick: () => {
            setVerificationSent(false);
            setMode("sign-in");
            setShowEmailForm(true);
            setPassword("");
          },
          className: "text-sm text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-300",
          children: "Back to sign in"
        })]
      })
    });
  }
  return _jsx(LoginStepContainer, {
    partnerId: partnerId,
    children: mode === "forgot-password" ? _jsxs("div", {
      className: "mb-6 w-full space-y-3 px-3",
      children: [_jsx("p", {
        className: "text-center text-sm font-medium text-gray-900 dark:text-gray-100",
        children: "Reset password"
      }), _jsx("p", {
        className: "text-center text-xs text-gray-500",
        children: resetEmailSent ? "We've sent a password reset link to your email" : "Enter your email and we'll send you a reset link"
      }), resetEmailSent ? _jsx("div", {
        className: "space-y-3 py-4 text-center",
        children: _jsx("p", {
          className: "text-sm text-green-600",
          children: "Check your email for a reset link."
        })
      }) : _jsxs(_Fragment, {
        children: [_jsx(Input, {
          type: "email",
          placeholder: "you@example.com",
          value: email,
          onChange: event => setEmail(event.target.value),
          disabled: isLoading,
          onKeyDown: event_0 => {
            if (event_0.key === "Enter") handleForgotPassword();
          }
        }), error && _jsx("p", {
          className: "text-sm text-red-500",
          children: error
        }), _jsx(Button, {
          onClick: handleForgotPassword,
          disabled: isLoading,
          className: "w-full",
          children: isLoading ? "Sending..." : "Send reset link"
        })]
      }), _jsx("button", {
        onClick: () => {
          setMode("sign-in");
          setShowEmailForm(true);
          setError(null);
          setResetEmailSent(false);
        },
        className: "w-full text-center text-sm text-gray-500 hover:text-gray-700",
        children: "Back to sign in"
      })]
    }) : showEmailForm ? _jsxs("div", {
      className: "mb-6 w-full space-y-3 px-3",
      children: [_jsx("p", {
        className: "text-center text-sm font-medium text-gray-900 dark:text-gray-100",
        children: mode === "sign-in" ? "Sign in with email" : "Create an account"
      }), mode === "sign-up" && _jsx(Input, {
        type: "text",
        placeholder: "Your name",
        value: name,
        onChange: event_1 => setName(event_1.target.value),
        disabled: isLoading
      }), _jsx(Input, {
        type: "email",
        placeholder: "you@example.com",
        value: email,
        onChange: event_2 => setEmail(event_2.target.value),
        disabled: isLoading
      }), _jsx(PasswordInput, {
        value: password,
        onChange: event_3 => setPassword(event_3.target.value),
        disabled: isLoading,
        onKeyDown: event_4 => {
          if (event_4.key === "Enter") handleEmailSubmit();
        },
        className: "pr-11"
      }, mode), error && _jsx("p", {
        className: "text-sm text-red-500",
        children: error
      }), _jsx(Button, {
        onClick: handleEmailSubmit,
        disabled: isLoading,
        className: "w-full",
        children: isLoading ? "Loading..." : mode === "sign-in" ? "Sign in" : "Sign up"
      }), mode === "sign-in" && _jsx("button", {
        onClick: () => {
          setMode("forgot-password");
          setError(null);
        },
        disabled: isLoading,
        className: "w-full text-center text-xs text-gray-500 hover:text-gray-700",
        children: "Forgot password?"
      }), _jsx("button", {
        onClick: () => {
          setMode(mode === "sign-in" ? "sign-up" : "sign-in");
          setError(null);
        },
        disabled: isLoading,
        className: "w-full text-center text-sm text-gray-500 hover:text-gray-700",
        children: mode === "sign-in" ? "Don't have an account? Sign up" : "Already have an account? Sign in"
      }), _jsx(Button, {
        variant: "outline",
        onClick: () => {
          setShowEmailForm(false);
          setError(null);
        },
        disabled: isLoading,
        className: "w-full",
        children: "Back"
      })]
    }) : _jsxs("div", {
      className: "mb-6 w-full space-y-4 px-3",
      children: [_jsx("div", {
        className: "grid w-full grid-cols-3 gap-4",
        children: SOCIAL_PROVIDERS.map(provider_0 => _jsx(AuthButton, {
          strategy: provider_0.id,
          onClick: () => handleSocialSignIn(provider_0.id),
          isLoading: isLoading
        }, provider_0.id))
      }), _jsxs("div", {
        className: "flex items-center gap-3",
        children: [_jsx("div", {
          className: "h-px flex-1 bg-gray-200"
        }), _jsx("span", {
          className: "text-xs text-gray-400",
          children: "or"
        }), _jsx("div", {
          className: "h-px flex-1 bg-gray-200"
        })]
      }), _jsx(AuthButton, {
        strategy: "email",
        onClick: () => setShowEmailForm(true),
        isLoading: isLoading
      }), error && _jsx("p", {
        className: "text-center text-sm text-red-500",
        children: error
      })]
    })
  });
}